/**
 * @file outsideHaulierPoh.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-26
 * @modified 2026-10-26
 * @description The planning boards' half of "an outside haulier chosen, and
 *              no PO-H" (Cam, 26 Oct 2026 — "If user chooses a haulier other
 *              than Excel agri in transport planning, and there is no PO-H
 *              raised for that order, can it prompt them to raise and send
 *              one"): which rows a haulier change asks about, and asking
 *              once per change. What is asked, and every refusal, is the
 *              server's (utils/outsideHaulierPoh.js, GET
 *              /api/transport/planning/outside-haulier-poh).
 *
 *   Asked once per change: an order is asked about the first time an outside
 *   haulier is set on it; "Not now" (or anything else) is not asked again
 *   for that order until its haulier changes again in this tab — to another
 *   outside haulier, or away to ours / none and back. Kept for the tab's
 *   life (module scope), so leaving the board and coming back does not ask
 *   again either.
 */

import { isExcelHaulier } from './planningRows';

const asked = new Map();
const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());

/** Why a row is never asked about (its own rules), or null. */
export function rowSkipReason(row) {
  if (!row) return 'no_row';
  if (row.is_self_billed) return 'self_billed';
  if (row.is_redelivery) return 'redelivery';
  if (row.is_haulage_job || String(row.order_line_id || '').startsWith('HO-')) return 'haulage_job';
  if (!Number.isInteger(Number(row.id)) || Number(row.id) <= 0) return 'no_order';
  return null;
}

/**
 * A haulier set on an order (from the board): should the board ask now?
 * Our own haulier or none forgets the order, so choosing an outside one
 * again asks again. The same outside haulier as last asked: no.
 */
export function noteHaulierChange(orderId, haulier) {
  const id = Number(orderId);
  if (!Number.isInteger(id) || id <= 0) return false;
  const h = norm(haulier);
  if (!h || isExcelHaulier(haulier)) { asked.delete(id); return false; }
  if (asked.get(id) === h) return false;
  asked.set(id, h);
  return true;
}

/**
 * The orders to ask about after `haulier` was set on these rows — one per
 * order, in the rows' order, each asked once per change.
 * @returns {Array<{ orderId, orderNumber, haulier }>}
 */
export function ordersToAsk(rows, haulier) {
  const out = [];
  const seen = new Set();
  for (const row of rows || []) {
    if (rowSkipReason(row)) continue;
    const id = Number(row.id);
    if (seen.has(id)) continue;
    seen.add(id);
    if (noteHaulierChange(id, haulier)) out.push({ orderId: id, orderNumber: row.order_number || null, haulier: String(haulier).trim() });
  }
  return out;
}

/** For tests: forget everything asked. */
export function resetAsked() { asked.clear(); }
